import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import AppLayout from "./components/AppLayout";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const EntryListPage = lazy(() => import("./pages/EntryListPage"));
const EntryFormPage = lazy(() => import("./pages/EntryFormPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

/** Wraps the entire app in context providers. Used as the root layout route. */
function RootLayout() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Suspense fallback={<div>Loading…</div>}>
          <Outlet />
        </Suspense>
      </AuthProvider>
    </ThemeProvider>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <AppLayout>{children}</AppLayout> : <Navigate to="/login" replace />;
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <Navigate to="/dashboard" replace /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/verify-email", element: <VerifyEmailPage /> },
      {
        path: "/dashboard",
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/entries",
        element: (
          <ProtectedRoute>
            <EntryListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/entries/new",
        element: (
          <ProtectedRoute>
            <EntryFormPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/entries/:id/edit",
        element: (
          <ProtectedRoute>
            <EntryFormPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings",
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
